import Constants from 'expo-constants';
import { z } from 'zod';

const baseEnvSchema = z.object({
  EXPO_PUBLIC_ALGOLIA_APPLICATION_ID: z.string(),
  EXPO_PUBLIC_ALGOLIA_INDEX_NAME: z.string(),
  EXPO_PUBLIC_ALGOLIA_SEARCH_API_KEY: z.string(),
  EXPO_PUBLIC_PUSH_NOTIFICATIONS_LOCALHOST: z.enum(['true', 'false']).optional(),
  EXPO_PUBLIC_APP_BUNDLE_ID: z.string(),
  EXPO_PUBLIC_APP_ENV: z.string(),
  EXPO_PUBLIC_APP_NAME: z.string(),
  EXPO_PUBLIC_APP_STORE_CONNECT_ID: z.string(),
  EXPO_PUBLIC_APP_WEB_DOMAIN: z.string(),
  EXPO_PUBLIC_AUTH_CALLBACK_URL: z.string(),
  EXPO_PUBLIC_AUTH_LOGOUT_URL: z.string(),
  EXPO_PUBLIC_BASE_API_URL: z.string().url(),
  EXPO_PUBLIC_REALTIME_HOST: z.string().regex(/^[a-z0-9.-]+$/i),
  EXPO_PUBLIC_COGNITO_CLIENT_ID: z.string(),
  EXPO_PUBLIC_COGNITO_USER_POOL: z.string(),
  EXPO_PUBLIC_COGNITO_LOGIN_DOMAIN: z.string(),
  EXPO_PUBLIC_EXPO_PROJECT_ID: z.string(),
  EXPO_PUBLIC_EXPO_UPDATES_URL: z.union([z.string().url(), z.literal('')]),
  EXPO_PUBLIC_FLAGSMITH_SDK_KEY: z.string(),
  EXPO_PUBLIC_MOD_EXPERT_ID: z.coerce.number(),
  EXPO_PUBLIC_CONTENT_LIBRARY_PARTNERSHIP_SURVEY_ID: z.string().optional(),
  EXPO_PUBLIC_ONBOARDING_SURVEY_GROUP_ID: z.string().optional(),
  EXPO_PUBLIC_ONE_SIGNAL_APP_ID: z.string(),
  EXPO_PUBLIC_ONE_SIGNAL_WEB_SAFARI_ID: z.string(),
  EXPO_PUBLIC_SEGMENT_WEB_WRITE_KEY: z.string().optional(),
  EXPO_PUBLIC_SEGMENT_WRITE_KEY: z.string().optional(),
  EXPO_PUBLIC_SEGMENT_ENABLED: z.enum(['true', 'false']).optional(),
  EXPO_PUBLIC_CLOUDFRONT_DOMAIN: z.string().optional(),
  EXPO_PUBLIC_SENTRY_DSN: z.union([z.string().url(), z.literal('')]).optional(),
  EXPO_PUBLIC_SENTRY_ENABLED: z.enum(['true', 'false']).optional(),
  EXPO_PUBLIC_CLARITY_PROJECT_ID: z.string().optional(),
  EXPO_PUBLIC_CLARITY_MOBILE_PROJECT_ID: z.string().optional(),
});

const envSchema = baseEnvSchema.superRefine((value, ctx) => {
  const isSegmentEnabled = value.EXPO_PUBLIC_SEGMENT_ENABLED !== 'false';

  const isSentryEnabled = value.EXPO_PUBLIC_SENTRY_ENABLED !== 'false';

  if (isSegmentEnabled) {
    if (!value.EXPO_PUBLIC_SEGMENT_WRITE_KEY) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Required when Segment is enabled',
        path: ['EXPO_PUBLIC_SEGMENT_WRITE_KEY'],
      });
    }

    if (!value.EXPO_PUBLIC_SEGMENT_WEB_WRITE_KEY) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Required when Segment is enabled',
        path: ['EXPO_PUBLIC_SEGMENT_WEB_WRITE_KEY'],
      });
    }
  }

  if (isSentryEnabled && !value.EXPO_PUBLIC_SENTRY_DSN) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Required when Sentry is enabled',
      path: ['EXPO_PUBLIC_SENTRY_DSN'],
    });
  }
});

const keys = [...baseEnvSchema.keyof().options];

const runtimeEnv = keys.reduce((acc, k) => {
  const expoExtraValue = Constants.expoConfig?.extra?.[k];

  return {
    ...acc,
    // Expo runtime uses expoConfig.extra; Node/Vitest/CI may only expose process.env.
    [k]: expoExtraValue ?? process.env[k],
  };
}, {});

const env = envSchema.parse(runtimeEnv);

export { env };
